import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Copy,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  QUESTION_TYPES,
  type FeedbackQuestion,
  type QuestionType,
} from "./contract";
import { newQuestion, TYPE_LABELS } from "./templates";
export function QuestionsEditor({
  questions,
  onChange,
  locked,
}: {
  questions: FeedbackQuestion[];
  onChange: (questions: FeedbackQuestion[]) => void;
  locked: boolean;
}) {
  const [active, setActive] = useState<string | undefined>(questions[0]?.id),
    [adding, setAdding] = useState(false);
  const change = (id: string, update: Partial<FeedbackQuestion>) =>
    onChange(questions.map((q) => (q.id === id ? { ...q, ...update } : q)));
  const move = (index: number, delta: number) => {
    const next = [...questions];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    onChange(next);
  };
  const remove = (id: string) => {
    onChange(
      questions
        .filter((q) => q.id !== id)
        .map((q) =>
          q.condition?.questionId === id ? { ...q, condition: null } : q,
        ),
    );
  };
  return (
    <div data-tour="forms-questions" className="space-y-3">
      {locked && (
        <div className="subcard p-4 text-xs text-glass-400">
          Questions are locked once responses arrive, so every answer keeps its
          meaning. Duplicate this form to start a new version.
        </div>
      )}
      {questions.map((q, index) => (
        <section key={q.id} className="glass-card overflow-hidden">
          <button
            type="button"
            className="flex w-full items-center gap-3 p-4 text-left"
            onClick={() => setActive(active === q.id ? undefined : q.id)}
            aria-expanded={active === q.id}
          >
            <span className="text-xs text-accent-300 tabular-nums">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium truncate">
                {q.title || "Untitled question"}
                {q.required ? " *" : ""}
              </span>
              <span className="text-[10px] text-glass-400">
                {TYPE_LABELS[q.type]}
                {q.condition ? " · Conditional" : ""}
              </span>
            </span>
            <ChevronDown className="w-4 h-4 text-glass-400" />
          </button>
          {active === q.id && (
            <fieldset
              disabled={locked}
              className="px-4 pb-4 space-y-4 border-t border-glass-200/10 pt-4 disabled:opacity-70"
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_155px]">
                <label className="text-xs space-y-1.5">
                  <span className="text-glass-400">
                    {q.type === "section" ? "Section heading" : "Question"}
                  </span>
                  <input
                    autoFocus
                    className="glass-input w-full"
                    value={q.title}
                    maxLength={300}
                    onChange={(e) => change(q.id, { title: e.target.value })}
                  />
                </label>
                <label className="text-xs space-y-1.5">
                  <span className="text-glass-400">Answer type</span>
                  <select
                    className="glass-select w-full"
                    value={q.type}
                    onChange={(e) =>
                      change(q.id, {
                        type: e.target.value as QuestionType,
                        ...(["rating", "scale"].includes(e.target.value)
                          ? { min: 1, max: 5 }
                          : {}),
                        required:
                          e.target.value === "section" ? false : q.required,
                      })
                    }
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block text-xs space-y-1.5">
                <span className="text-glass-400">
                  Helpful context{" "}
                  <span className="text-glass-500">(optional)</span>
                </span>
                <textarea
                  className="glass-input w-full resize-y"
                  rows={2}
                  value={q.description}
                  maxLength={2000}
                  onChange={(e) =>
                    change(q.id, { description: e.target.value })
                  }
                  placeholder="An example or a little explanation…"
                />
              </label>
              {["single", "multiple", "dropdown", "grid"].includes(q.type) && (
                <div className="space-y-2">
                  <p className="text-xs text-glass-400">Choices</p>
                  {q.options.map((option, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-xs text-glass-500 py-3 w-4">
                        {i + 1}
                      </span>
                      <input
                        className="glass-input flex-1 min-w-0"
                        aria-label={`Option ${i + 1}`}
                        value={option}
                        maxLength={200}
                        onChange={(e) =>
                          change(q.id, {
                            options: q.options.map((o, n) =>
                              n === i ? e.target.value : o,
                            ),
                          })
                        }
                      />
                      <button
                        type="button"
                        className="btn-ghost"
                        aria-label={`Remove option ${i + 1}`}
                        disabled={q.options.length <= 2}
                        onClick={() =>
                          change(q.id, {
                            options: q.options.filter((_, n) => n !== i),
                          })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn-ghost text-xs"
                    disabled={q.options.length >= 30}
                    onClick={() =>
                      change(q.id, {
                        options: [
                          ...q.options,
                          `Option ${q.options.length + 1}`,
                        ],
                      })
                    }
                  >
                    <Plus className="w-3 h-3" />
                    Add choice
                  </button>
                </div>
              )}
              {q.type === "grid" && (
                <div className="space-y-2">
                  <p className="text-xs text-glass-400">Rows</p>
                  {q.rows.map((row, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        aria-label={`Grid row ${i + 1}`}
                        className="glass-input flex-1 min-w-0"
                        value={row}
                        onChange={(e) =>
                          change(q.id, {
                            rows: q.rows.map((r, n) =>
                              n === i ? e.target.value : r,
                            ),
                          })
                        }
                      />
                      <button
                        className="btn-ghost"
                        aria-label={`Remove grid row ${i + 1}`}
                        disabled={q.rows.length <= 1}
                        onClick={() =>
                          change(q.id, {
                            rows: q.rows.filter((_, n) => n !== i),
                          })
                        }
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn-ghost text-xs"
                    disabled={q.rows.length >= 20}
                    onClick={() =>
                      change(q.id, {
                        rows: [...q.rows, `Item ${q.rows.length + 1}`],
                      })
                    }
                  >
                    <Plus className="w-3 h-3" />
                    Add row
                  </button>
                </div>
              )}
              {["rating", "scale", "number"].includes(q.type) && (
                <div className="grid grid-cols-2 gap-3">
                  {(["min", "max"] as const).map((key) => (
                    <label className="text-xs space-y-1.5" key={key}>
                      <span className="text-glass-400">
                        {key === "min" ? "Minimum" : "Maximum"}
                      </span>
                      <input
                        className="glass-input w-full"
                        type="number"
                        value={q[key]}
                        onChange={(e) =>
                          change(q.id, { [key]: Number(e.target.value) })
                        }
                      />
                    </label>
                  ))}
                </div>
              )}
              {q.type !== "section" && (
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) =>
                      change(q.id, { required: e.target.checked })
                    }
                  />
                  Required answer
                </label>
              )}
              <details className="text-xs">
                <summary className="text-glass-400 cursor-pointer py-2">
                  Show this question only when…
                </summary>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  <select
                    className="glass-select min-w-0"
                    aria-label="Conditional question"
                    value={q.condition?.questionId || ""}
                    onChange={(e) =>
                      change(q.id, {
                        condition: e.target.value
                          ? {
                              questionId: e.target.value,
                              equals: questions.find(
                                (x) => x.id === e.target.value,
                              )!.options[0],
                            }
                          : null,
                      })
                    }
                  >
                    <option value="">Always show</option>
                    {questions
                      .slice(0, index)
                      .filter((x) =>
                        ["single", "dropdown", "multiple"].includes(x.type),
                      )
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.title}
                        </option>
                      ))}
                  </select>
                  {q.condition && (
                    <select
                      className="glass-select min-w-0"
                      aria-label="Show when answer is"
                      value={q.condition.equals}
                      onChange={(e) =>
                        change(q.id, {
                          condition: {
                            ...q.condition!,
                            equals: e.target.value,
                          },
                        })
                      }
                    >
                      {questions
                        .find((x) => x.id === q.condition?.questionId)
                        ?.options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  )}
                </div>
                <p className="text-glass-400 mt-2">
                  Choose an earlier choice question. Hidden questions never
                  require an answer.
                </p>
              </details>
              <div className="flex items-center justify-between border-t border-glass-200/10 pt-3">
                <div className="flex gap-1">
                  <button
                    className="btn-ghost"
                    aria-label="Move question up"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    className="btn-ghost"
                    aria-label="Move question down"
                    disabled={index === questions.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-1">
                  <button
                    className="btn-ghost text-xs"
                    disabled={questions.length >= 50}
                    onClick={() => {
                      const copy = {
                        ...q,
                        id: crypto.randomUUID(),
                        title: `${q.title} (copy)`,
                      };
                      const next = [...questions];
                      next.splice(index + 1, 0, copy);
                      onChange(next);
                      setActive(copy.id);
                    }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Duplicate
                  </button>
                  <button
                    className="btn-ghost"
                    aria-label="Delete question"
                    disabled={questions.length <= 1}
                    onClick={() => remove(q.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </fieldset>
          )}
        </section>
      ))}
      {!locked && (
        <div>
          <button
            className="btn-primary w-full justify-center min-h-[44px]"
            disabled={questions.length >= 50}
            onClick={() => setAdding(!adding)}
          >
            <Plus className="w-4 h-4" />
            Add question
          </button>
          {adding && (
            <div className="glass-card grid grid-cols-2 sm:grid-cols-3 gap-1 p-2 mt-2">
              {QUESTION_TYPES.map((type) => (
                <button
                  key={type}
                  className="btn-ghost text-xs justify-start min-h-[44px]"
                  onClick={() => {
                    const q = newQuestion(
                      type,
                      type === "section"
                        ? "A little more detail"
                        : "Untitled question",
                    );
                    onChange([...questions, q]);
                    setActive(q.id);
                    setAdding(false);
                  }}
                >
                  {TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
